import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useRegisterOwnerMutation, useCheckSlugQuery } from '../../services/api';
import { setCredentials } from '../../store/slices/authSlice';
import { validateEmail, validateMobile } from '../../utils/helpers';
import Loading from '../../components/Loading/Loading';
import './Register.css';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    restaurantName: '',
    address: '',
    password: '',
    confirmPassword: '',
    themeColor: '#3B82F6'
  });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [slugStatus, setSlugStatus] = useState({ 
    available: null, 
    loading: false, 
    message: '' 
  });
  
  const [register, { isLoading }] = useRegisterOwnerMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Check slug availability when restaurant name changes
  const { data: slugData, isLoading: checkingSlug, error: slugError } = useCheckSlugQuery(
    formData.restaurantName, 
    { 
      skip: !formData.restaurantName || formData.restaurantName.length < 2 
    }
  );

  // Update slug status when API response changes
  useEffect(() => {
    if (checkingSlug) {
      setSlugStatus({
        available: null,
        loading: true,
        message: 'Checking availability...'
      });
    } else if (slugData) {
      const { available, reserved, suggestedSlug, message } = slugData.data;
      setSlugStatus({
        available: available && !reserved,
        loading: false,
        message: message,
        suggestedSlug: suggestedSlug
      });
    } else if (slugError) {
      setSlugStatus({
        available: null,
        loading: false,
        message: 'Error checking availability'
      });
    } else if (!formData.restaurantName || formData.restaurantName.length < 2) {
      setSlugStatus({
        available: null,
        loading: false,
        message: ''
      });
    }
  }, [slugData, checkingSlug, slugError, formData.restaurantName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, logo: 'File size must be less than 5MB' }));
        return;
      }
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, logo: 'Please select an image file' }));
        return;
      }
      setLogo(file);
      setErrors(prev => ({ ...prev, logo: '' }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogo(null);
    setLogoPreview(null);
  };

  const validateForm = () => {
    const newErrors = {};

    // Personal info validation
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.mobile) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!validateMobile(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid mobile number';
    }

    // Restaurant info validation
    if (!formData.restaurantName.trim()) {
      newErrors.restaurantName = 'Restaurant name is required';
    } else if (formData.restaurantName.length < 2) {
      newErrors.restaurantName = 'Restaurant name must be at least 2 characters';
    } else if (slugStatus.loading) {
      newErrors.restaurantName = 'Please wait while we check availability...';
    } else if (slugStatus.available === false) {
      newErrors.restaurantName = 'Restaurant name is not available. Please choose another name.';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const submitData = {
        ...formData,
        logo: logo
      };

      const result = await register(submitData).unwrap();
      
      if (result.success) {
        dispatch(setCredentials(result.data));
        navigate('/owner');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ 
        submit: error.data?.message || 'Registration failed. Please try again.' 
      });
    }
  };

  const nextStep = () => {
    if (currentStep === 1) {
      // Validate step 1
      const stepErrors = {};
      if (!formData.name.trim()) stepErrors.name = 'Full name is required';
      if (!formData.email) stepErrors.email = 'Email is required';
      if (!formData.mobile) stepErrors.mobile = 'Mobile number is required';
      if (!validateEmail(formData.email)) stepErrors.email = 'Please enter a valid email';
      if (!validateMobile(formData.mobile)) stepErrors.mobile = 'Please enter a valid mobile number';
      
      if (Object.keys(stepErrors).length === 0) {
        setCurrentStep(2);
      } else {
        setErrors(stepErrors);
      }
    } else if (currentStep === 2) {
      // Validate step 2
      const stepErrors = {};
      if (!formData.restaurantName.trim()) stepErrors.restaurantName = 'Restaurant name is required';
      if (!formData.address.trim()) stepErrors.address = 'Address is required';
      if (slugStatus.available === false) stepErrors.restaurantName = 'Restaurant name is not available';
      
      if (Object.keys(stepErrors).length === 0) {
        setCurrentStep(3);
      } else {
        setErrors(stepErrors);
      }
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const getSlugMessageType = () => {
    if (slugStatus.loading) return 'info';
    if (slugStatus.available === true) return 'success';
    if (slugStatus.available === false) return 'error';
    return 'info';
  };

  const steps = [
    { number: 1, title: 'Personal Info' },
    { number: 2, title: 'Restaurant Details' },
    { number: 3, title: 'Security' }
  ];

  return (
    <div className="register-container">
      <div className="register-card">
        {/* Header */}
        <div className="register-header">
          <div className="logo">
            <span className="logo-icon">🍽️</span>
            <span className="logo-text">Cafe.WebReady24</span>
          </div>
          <h1>Start Your Restaurant Journey</h1>
          <p>Join thousands of restaurants growing their business online</p>
        </div>

        {/* Modern Progress Steps */}
        <div className="modern-progress">
          <div className="progress-track">
            <div 
              className="progress-fill"
              style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
            ></div>
          </div>
          <div className="steps-container">
            {steps.map((step, index) => (
              <div 
                key={step.number} 
                className={`modern-step ${currentStep > step.number ? 'completed' : ''} ${currentStep === step.number ? 'active' : ''}`}
              >
                <div className="step-indicator">
                  <div className="step-icon">
                    {currentStep > step.number ? (
                      <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <span className="step-number">{step.number}</span>
                    )}
                  </div>
                  <div className="step-label">{step.title}</div>
                </div>
                {index < steps.length - 1 && (
                  <div className="step-connector"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {errors.submit && (
          <div className="alert alert-error">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="register-form">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="form-step">
              <div className="step-header">
                <h2>Tell us about yourself</h2>
                <p>We'll use this information to create your account</p>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-control ${errors.name ? 'error' : ''}`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && (
                    <div className="form-error">{errors.name}</div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-control ${errors.email ? 'error' : ''}`}
                    placeholder="your@email.com"
                  />
                  {errors.email && (
                    <div className="form-error">{errors.email}</div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="mobile" className="form-label">
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    className={`form-control ${errors.mobile ? 'error' : ''}`}
                    placeholder="+91 98765 43210"
                  />
                  {errors.mobile && (
                    <div className="form-error">{errors.mobile}</div>
                  )}
                </div>
              </div>

              <div className="step-actions">
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn btn-primary btn-full"
                >
                  Continue to Restaurant Details
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Restaurant Information */}
          {currentStep === 2 && (
            <div className="form-step">
              <div className="step-header">
                <h2>About your restaurant</h2>
                <p>This information will be visible to your customers</p>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="restaurantName" className="form-label">
                    Restaurant Name *
                  </label>
                  <input
                    type="text"
                    id="restaurantName"
                    name="restaurantName"
                    value={formData.restaurantName}
                    onChange={handleChange}
                    className={`form-control ${errors.restaurantName ? 'error' : ''}`}
                    placeholder="Enter your restaurant name"
                  />
                  {errors.restaurantName && (
                    <div className="form-error">{errors.restaurantName}</div>
                  )}
                  {slugStatus.message && (
                    <div className={`slug-status ${getSlugMessageType()}`}>
                      <span className="status-icon">
                        {slugStatus.loading ? '⏳' : 
                         slugStatus.available ? '✅' : '❌'}
                      </span>
                      {slugStatus.message}
                      {slugStatus.suggestedSlug && slugStatus.available === false && (
                        <div className="suggestion">Try: <strong>{slugStatus.suggestedSlug}</strong></div>
                      )}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="address" className="form-label">
                    Restaurant Address *
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="3"
                    className={`form-control ${errors.address ? 'error' : ''}`}
                    placeholder="Enter your complete restaurant address"
                  />
                  {errors.address && (
                    <div className="form-error">{errors.address}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Brand Customization</label>
                  <div className="brand-customization">
                    <div className="logo-upload-section">
                      <label className="upload-label">Restaurant Logo</label>
                      <div className="upload-area">
                        {logoPreview ? (
                          <div className="logo-preview">
                            <img src={logoPreview} alt="Logo preview" />
                            <button type="button" onClick={removeLogo} className="remove-logo">
                              ×
                            </button>
                          </div>
                        ) : (
                          <div className="upload-placeholder">
                            <div className="upload-icon">📁</div>
                            <div className="upload-text">
                              <span>Click to upload logo</span>
                              <small>PNG, JPG up to 5MB</small>
                            </div>
                            <input
                              type="file"
                              onChange={handleLogoChange}
                              accept="image/*"
                              className="file-input"
                            />
                          </div>
                        )}
                      </div>
                      {errors.logo && (
                        <div className="form-error">{errors.logo}</div>
                      )}
                    </div>

                    <div className="color-picker-section">
                      <label className="color-label">Theme Color</label>
                      <div className="color-input-group">
                        <input
                          type="color"
                          id="themeColor"
                          name="themeColor"
                          value={formData.themeColor}
                          onChange={handleChange}
                          className="color-picker"
                        />
                        <div className="color-info">
                          <span className="color-value">{formData.themeColor}</span>
                          <small>This color will be used throughout your store</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="step-actions">
                <button
                  type="button"
                  onClick={prevStep}
                  className="btn btn-secondary"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn btn-primary"
                  disabled={slugStatus.loading}
                >
                  {slugStatus.loading ? 'Checking...' : 'Continue to Security'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Security */}
          {currentStep === 3 && (
            <div className="form-step">
              <div className="step-header">
                <h2>Secure your account</h2>
                <p>Create a strong password to protect your restaurant</p>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="password" className="form-label">
                    Password *
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`form-control ${errors.password ? 'error' : ''}`}
                    placeholder="Create a strong password"
                  />
                  {errors.password && (
                    <div className="form-error">{errors.password}</div>
                  )}
                  <div className="password-strength">
                    <div className={`strength-bar ${formData.password.length >= 6 ? 'strong' : formData.password.length >= 3 ? 'medium' : 'weak'}`}>
                      <div className="strength-fill"></div>
                    </div>
                    <small>Minimum 6 characters</small>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword" className="form-label">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`form-control ${errors.confirmPassword ? 'error' : ''}`}
                    placeholder="Confirm your password"
                  />
                  {errors.confirmPassword && (
                    <div className="form-error">{errors.confirmPassword}</div>
                  )}
                  {formData.confirmPassword && formData.password === formData.confirmPassword && (
                    <div className="password-match">✓ Passwords match</div>
                  )}
                </div>
              </div>

              <div className="step-summary">
                <h3>Almost there! Review your details:</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <label>Restaurant Name:</label>
                    <span>{formData.restaurantName}</span>
                  </div>
                  <div className="summary-item">
                    <label>Your Name:</label>
                    <span>{formData.name}</span>
                  </div>
                  <div className="summary-item">
                    <label>Email:</label>
                    <span>{formData.email}</span>
                  </div>
                  <div className="summary-item">
                    <label>Store URL:</label>
                    <span className="store-url">cafe.webready24.com/{slugify(formData.restaurantName)}</span>
                  </div>
                </div>
              </div>

              <div className="step-actions">
                <button
                  type="button"
                  onClick={prevStep}
                  className="btn btn-secondary"
                >
                  Back
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary btn-success"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="spinner"></div>
                      Creating Your Restaurant...
                    </>
                  ) : (
                    '🎉 Launch My Restaurant'
                  )}
                </button>
              </div>
            </div>
          )}
        </form>

        <div className="register-footer">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="auth-link">
              Sign in to your restaurant
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// Helper function for slugify (you might want to move this to utils)
const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

export default Register;